import "react-native-url-polyfill/auto";
import { Slot } from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { useFonts } from "expo-font";
import { useEffect } from "react";
import { StatusBar } from "expo-status-bar";
import { configureReanimatedLogger, ReanimatedLogLevel } from "react-native-reanimated";
import { AppProvider } from "@/providers/AppProvider";
import * as Sentry from "@sentry/react-native";
import { isRunningInExpoGo } from "expo";
import { useNavigationContainerRef } from "@react-navigation/native";
import { Platform } from "react-native";
import Head from "expo-router/head";
import "@/global.css";

const navigationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: !isRunningInExpoGo(),
});

configureReanimatedLogger({
  level: ReanimatedLogLevel.warn,
  strict: false,
});

Sentry.init({
  dsn: "https://e1bbc3297e3e2950a84e7ebd02f8b79c@o4509253761302528.ingest.us.sentry.io/4510554726596608",
  // enabled: process.env.NODE_ENV === "production", // TODO: talk with team about this, check sentry logs or find way to distinguish.
  debug: false, // Disables Sentry's internal debug logger (noisy TTID warnings in dev). Actual errors still get captured.
  tracesSampleRate: 1.0, // Set tracesSampleRate to 1.0 to capture 100% of transactions for tracing. Adjusting this value in production.
  sendDefaultPii: true,
  integrations: [
    // Pass integration
    navigationIntegration,
  ],
  enableNativeFramesTracking: !isRunningInExpoGo() && Platform.OS !== "web", // Tracks slow and frozen frames in the application
});

function Root() {
  // Capture the NavigationContainer ref and register it with the integration.
  const ref = useNavigationContainerRef();
  const [loaded] = useFonts({
    SpaceMono: require("@/assets/fonts/SpaceMono-Regular.ttf"),
  });

  useEffect(() => {
    if (ref?.current) {
      navigationIntegration.registerNavigationContainer(ref);
    }
  }, [ref]);

  useEffect(() => {
    if (loaded) {
      SplashScreen.hideAsync();
    }
  }, [loaded]);

  if (!loaded) {
    return null;
  }

  const originalConsoleWarn = console.warn;
  const originalConsoleError = console.error;

  console.warn = (...args: string[]) => {
    // https://github.com/expo/expo/issues/33248
    if (args[0] === "props.pointerEvents is deprecated. Use style.pointerEvents") {
      return;
    }

    // Suppress Sentry TTID warnings in development
    if (process.env.NODE_ENV !== "production") {
      if (
        args[0]?.includes?.("Setting Root component creation timestamp after app start end is set") ||
        args[0]?.includes?.("Overwriting already set root component creation timestamp")
      ) {
        return;
      }
    }

    if (args[0].includes("findDOM")) console.log("found");
    originalConsoleWarn(...args);
  };

  console.error = (...args: string[]) => {
    if (
      args[0] ===
      "Warning: findDOMNode is deprecated and will be removed in the next major release. Instead, add a ref directly to the element you want to reference. Learn more about using refs safely here: https://reactjs.org/link/strict-mode-find-node%s"
    ) {
      return;
    }
    originalConsoleError(...args);
  };

  return (
    <AppProvider>
      <Head>
        <title>RockForce </title>
        <meta name="description" content="" />
      </Head>
      <Slot />
      <StatusBar style="auto" translucent={true} networkActivityIndicatorVisible={true} animated={true} />
    </AppProvider>
  );
}

// Wrap the Root Layout route component with `Sentry.wrap` to capture gesture info and profiling data.
// Errors can be thrown 2 ways to be caught `throw new Error("My first Sentry error!");` or `Sentry.nativeCrash();`
export default Sentry.wrap(Root);
// TODO: Read about sentry `debug symbols` and `source context`
